import React, { useEffect, useState } from 'react';
import { operationQueue } from '@/utils/operationQueue';
import { AlertCircle, CheckCircle, Loader2, RefreshCcw, Archive } from 'lucide-react';

/**
 * Operations Monitor - Shows active operations and their status
 * Useful for debugging and monitoring concurrent operations
 */
export const OperationsMonitor: React.FC<{
  collectionId?: string;
  visible?: boolean;
}> = ({ collectionId, visible = false }) => { // Keep the default as false to hide the component
  const [activeCount, setActiveCount] = useState(0);
  const [recentOperations, setRecentOperations] = useState<string[]>([]);
  const [duplicateOperations, setDuplicateOperations] = useState<string[]>([]);
  const [lastOperation, setLastOperation] = useState<string | null>(null);
  const [lastStatus, setLastStatus] = useState<'success' | 'error' | 'processing' | null>(null);
  const [completedOperations, setCompletedOperations] = useState(0);
  
  // We'll still track operations for debugging purposes, but won't display them
  useEffect(() => {
    // Update the active count every 500ms
    const interval = setInterval(() => {
      const count = operationQueue.activeCount;
      setActiveCount(count);
      
      // Get recent completed operations for debugging
      const recent = operationQueue.getRecentlyCompleted(30000);
      setRecentOperations(recent);
      
      // Identify potential duplicate operations (same prefix)
      const prefixMap = new Map<string, string[]>();
      for (const op of recent) {
        const prefix = op.split('-')[0];
        if (!prefixMap.has(prefix)) {
          prefixMap.set(prefix, []);
        }
        prefixMap.get(prefix)!.push(op);
      }
      
      const duplicates = Array.from(prefixMap.entries())
        .filter(([_, ops]) => ops.length > 1)
        .flatMap(([_, ops]) => ops);
      
      setDuplicateOperations(duplicates);
      
      // Get active operations for debugging
      if (count > 0) {
        const operations = operationQueue.getActiveOperations();
        console.log('Current active operations:', operations);
        
        // If the last status wasn't processing, update it
        if (lastStatus !== 'processing') {
          setLastOperation(`Operations in progress: ${operations.join(', ')}`);
          setLastStatus('processing');
        }
      }
      // When operations complete, update the status
      else if (lastStatus === 'processing') {
        setLastOperation(`Operations completed`);
        setLastStatus('success');
        setCompletedOperations(prev => prev + recent.length);
        
        // Reset after 3 seconds using callback form to avoid closure issues
        setTimeout(() => {
          setLastStatus(currentStatus => {
            if (currentStatus === 'success') {
              setLastOperation(null);
              return null;
            }
            return currentStatus;
          });
        }, 3000);
      }
    }, 500);
    
    // Clean up on unmount
    return () => clearInterval(interval);
  }, [lastStatus]);
  
  // Listen for changes in the operation queue for this specific collection
  useEffect(() => {
    if (!collectionId) return;
    
    // This function will check active operations and update UI accordingly
    const checkOperations = () => {
      const count = operationQueue.activeCount;
      const collectionOperations = operationQueue.getOperationsByPrefix(`improve-group-`);
      
      if (count > 0) {
        setLastOperation(`Operation in progress: ${collectionOperations.join(', ')}`);
        setLastStatus('processing');
      } else if (lastStatus === 'processing') {
        setLastOperation(`Operation completed`);
        setLastStatus('success');
        setCompletedOperations(prev => prev + 1);
        
        // Reset after 3 seconds using callback form to avoid closure issues
        setTimeout(() => {
          setLastStatus(currentStatus => {
            if (currentStatus === 'success') {
              setLastOperation(null);
              return null;
            }
            return currentStatus;
          });
        }, 3000);
      }
    };
    
    const interval = setInterval(checkOperations, 500);
    return () => clearInterval(interval);
  }, [collectionId, lastStatus]);
  
  // Always hide the component unless explicitly set to visible
  return null;
};
